import { Conditions, Fields } from '@root/constants'
import { IAccountingRule } from '@root/query'
import { ReactNode, useMemo, useState } from 'react'
import { Controller, get, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { FaAlignLeft, FaHashtag, FaPlus, FaTimes, FaUser } from 'react-icons/fa'
import {
  AndMark,
  ConditionLabel,
  ConditionLabelText,
  ConditionRow,
  ConditionsBox,
  ConditionsHelp,
  ConditionTitle,
  HelpCode,
  RemoveButton,
  TextWell,
  WellInput
} from './AccountingRuleForm.styled'
import AmountFilter, { getAmountRangeFromRules } from './AmountFilter'
import MultipleContactSelector from './ContactSelector/MultipleContactSelector'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

const SHOW_IBAN_FILTER = false

type FilterKind = Fields.DESCRIPTION | Fields.REFERENCE | Fields.CONTACT

const FiltersSelector: React.FC<{ isBlank?: boolean }> = ({ isBlank = false }) => {
  const [t] = useTranslation()
  const {
    control,
    register,
    getValues,
    setValue,
    clearErrors,
    formState: { errors, submitCount }
  } = useFormContext<IAccountingRule>()
  const pendingContactName = useWatch({ control, name: 'contact_name' })
  const [openContactDropdown, setOpenContactDropdown] = useState(false)

  const existingRules = getValues('rule_node.rules') ?? []
  const existingRange = getAmountRangeFromRules(existingRules)
  const hasExistingAmountRules = existingRange.min !== '' || existingRange.max !== ''
  const [amountFilterVisible, setAmountFilterVisible] = useState(isBlank || hasExistingAmountRules)

  const { append, fields, remove } = useFieldArray<IAccountingRule, 'rule_node.rules'>({
    control,
    name: 'rule_node.rules'
  })

  const filterDefaults: Record<FilterKind, { condition: Conditions; value: string | number[] }> = {
    [Fields.DESCRIPTION]: { condition: Conditions.CONTAINS, value: '' },
    [Fields.REFERENCE]: { condition: Conditions.CONTAINS, value: '' },
    [Fields.CONTACT]: { condition: Conditions.CONTACT_IS_IN, value: [] }
  }

  const usedFields = useMemo(() => {
    const collected = new Set<string>()
    fields.forEach(fieldItem => {
      if ('field' in fieldItem) {
        collected.add(`${fieldItem.field}-${fieldItem.condition}`)
      }
    })
    return collected
  }, [fields])

  const isUsed = (field: FilterKind) =>
    usedFields.has(`${field}-${filterDefaults[field].condition}`)

  const clearConditionError = () => {
    clearErrors('rule_node')
  }

  const addFilter = (field: FilterKind) => {
    if (isUsed(field)) return
    if (field === Fields.CONTACT) {
      setOpenContactDropdown(true)
    }
    append({
      field,
      condition: filterDefaults[field].condition,
      value: filterDefaults[field].value
    })
    clearConditionError()
  }

  const showAmount = () => {
    if (amountFilterVisible) return
    setAmountFilterVisible(true)
    clearConditionError()
  }

  const wildcardTitle = t('settings.automation.accountingRules.form.wildcardTitle')
  const rows: ReactNode[] = []

  fields.forEach((fieldItem, index) => {
    if (!('field' in fieldItem) || fieldItem.field === Fields.MONEY) return

    const removeField = () => {
      if (fieldItem.field === Fields.CONTACT) {
        setValue('contact_name', undefined)
        setOpenContactDropdown(false)
      }
      remove(index)
    }

    const showAnd = rows.length > 0

    if (fieldItem.field === Fields.DESCRIPTION || fieldItem.field === Fields.REFERENCE) {
      const isDescription = fieldItem.field === Fields.DESCRIPTION
      rows.push(
        <ConditionRow key={fieldItem.id} $divider={showAnd}>
          <ConditionLabel>
            {isDescription ? <FaAlignLeft /> : <FaHashtag />}
            <ConditionLabelText>
              {showAnd && (
                <AndMark>{t('settings.automation.accountingRules.form.conditionAnd')}</AndMark>
              )}
              <ConditionTitle>
                {t(
                  isDescription
                    ? 'settings.automation.accountingRules.form.conditionDescription'
                    : 'settings.automation.accountingRules.form.conditionReference'
                )}
              </ConditionTitle>
            </ConditionLabelText>
          </ConditionLabel>
          <ConditionValue>
            <TextWell $invalid={Boolean(get(errors, `rule_node.rules.${index}.value`))}>
              <WellInput
                placeholder={t(
                  isDescription
                    ? 'settings.automation.accountingRules.form.descriptionPlaceholder'
                    : 'settings.automation.accountingRules.form.referencePlaceholder'
                )}
                title={wildcardTitle}
                {...register(`rule_node.rules.${index}.value`)}
                autoComplete="off"
              />
            </TextWell>
            <ConditionValueError name={`rule_node.rules.${index}.value`} />
          </ConditionValue>
          <RemoveButton type="button" onClick={removeField} aria-label={t('general.delete')}>
            <FaTimes />
          </RemoveButton>
        </ConditionRow>
      )
      return
    }

    if (SHOW_IBAN_FILTER && fieldItem.field === Fields.IBAN) {
      rows.push(
        <ConditionRow key={fieldItem.id} $divider={showAnd}>
          <ConditionLabel>
            <ConditionLabelText>
              {showAnd && (
                <AndMark>{t('settings.automation.accountingRules.form.conditionAnd')}</AndMark>
              )}
              <ConditionTitle>
                {t('settings.automation.accountingRules.form.ibanHeader')}
              </ConditionTitle>
            </ConditionLabelText>
          </ConditionLabel>
          <ConditionValue>
            <TextWell $invalid={Boolean(get(errors, `rule_node.rules.${index}.value`))}>
              <WellInput
                placeholder={t('settings.automation.accountingRules.form.ibanLabel')}
                title={wildcardTitle}
                {...register(`rule_node.rules.${index}.value`)}
                autoComplete="off"
              />
            </TextWell>
            <ConditionValueError name={`rule_node.rules.${index}.value`} />
          </ConditionValue>
          <RemoveButton type="button" onClick={removeField} aria-label={t('general.delete')}>
            <FaTimes />
          </RemoveButton>
        </ConditionRow>
      )
      return
    }

    if (fieldItem.field === Fields.CONTACT) {
      rows.push(
        <ConditionRow key={fieldItem.id} $divider={showAnd}>
          <ConditionLabel>
            <FaUser />
            <ConditionLabelText>
              {showAnd && (
                <AndMark>{t('settings.automation.accountingRules.form.conditionAnd')}</AndMark>
              )}
              <ConditionTitle>
                {t('settings.automation.accountingRules.form.conditionContact')}
              </ConditionTitle>
            </ConditionLabelText>
          </ConditionLabel>
          <ControllerContact
            index={index}
            pendingContactName={pendingContactName}
            openOnMount={openContactDropdown}
            setValue={setValue}
            remove={remove}
          />
          <RemoveButton type="button" onClick={removeField} aria-label={t('general.delete')}>
            <FaTimes />
          </RemoveButton>
        </ConditionRow>
      )
    }
  })

  if (amountFilterVisible) {
    rows.push(
      <AmountFilter
        key="amount"
        showAnd={rows.length > 0}
        append={append}
        remove={remove}
        onClose={() => {
          setAmountFilterVisible(false)
        }}
      />
    )
  }

  const highlightEmpty = submitCount > 0 && Object.keys(errors).length > 0

  const chips: { key: string; label: string; onClick: () => void }[] = []
  if (!isUsed(Fields.DESCRIPTION)) {
    chips.push({
      key: 'description',
      label: t('settings.automation.accountingRules.form.chipDescription'),
      onClick: () => addFilter(Fields.DESCRIPTION)
    })
  }
  if (!isUsed(Fields.REFERENCE)) {
    chips.push({
      key: 'reference',
      label: t('settings.automation.accountingRules.form.chipReference'),
      onClick: () => addFilter(Fields.REFERENCE)
    })
  }
  if (!isUsed(Fields.CONTACT)) {
    chips.push({
      key: 'contact',
      label: t('settings.automation.accountingRules.form.chipContact'),
      onClick: () => addFilter(Fields.CONTACT)
    })
  }
  if (!amountFilterVisible) {
    chips.push({
      key: 'amount',
      label: t('settings.automation.accountingRules.form.chipAmount'),
      onClick: showAmount
    })
  }
  if (SHOW_IBAN_FILTER && !usedFields.has(`${Fields.IBAN}-${Conditions.CONTAINS}`)) {
    chips.push({
      key: 'iban',
      label: t('settings.automation.accountingRules.form.ibanLabel'),
      onClick: () => {
        append({ field: Fields.IBAN, condition: Conditions.CONTAINS, value: '' })
        clearConditionError()
      }
    })
  }

  return (
    <StyledConditions>
      {rows.length === 0 ? (
        <EmptyConditions $invalid={highlightEmpty}>
          {t('settings.automation.accountingRules.form.conditionsEmpty')}
        </EmptyConditions>
      ) : (
        <>
          <ConditionsBox>{rows}</ConditionsBox>
          <ConditionsHelp>
            <Trans
              i18nKey="settings.automation.accountingRules.form.conditionsHelp"
              components={{ code: <HelpCode /> }}
            />
          </ConditionsHelp>
        </>
      )}

      {chips.length > 0 && (
        <ChipRow>
          <ChipLabel>{t('settings.automation.accountingRules.form.addCondition')}</ChipLabel>
          {chips.map(chip => (
            <AddChip key={chip.key} type="button" onClick={chip.onClick}>
              <FaPlus />
              {chip.label}
            </AddChip>
          ))}
        </ChipRow>
      )}
    </StyledConditions>
  )
}

const ControllerContact: React.FC<{
  index: number
  pendingContactName?: string
  openOnMount: boolean
  setValue: ReturnType<typeof useFormContext<IAccountingRule>>['setValue']
  remove: (index: number) => void
}> = ({ index, pendingContactName, openOnMount, setValue, remove }) => {
  const { control } = useFormContext<IAccountingRule>()

  return (
    <Controller
      control={control}
      name={`rule_node.rules.${index}.value`}
      render={({ field, fieldState: { error } }) => (
        <ConditionValue>
          <MultipleContactSelector
            value={field.value as number[]}
            pendingContactName={pendingContactName}
            openOnMount={openOnMount}
            invalid={Boolean(error)}
            onChange={contactIds => {
              if (contactIds.length === 0) {
                setValue('contact_name', undefined)
                remove(index)
                return
              }
              field.onChange(contactIds)
              setValue('contact_name', undefined)
            }}
            onPendingContactDismiss={() => {
              const contactIds = (field.value as number[]) || []
              setValue('contact_name', undefined)
              if (contactIds.length === 0) {
                remove(index)
              }
            }}
          />
          <ConditionValueError name={`rule_node.rules.${index}.value`} />
        </ConditionValue>
      )}
    />
  )
}

const ConditionValueError: React.FC<{ name: string }> = ({ name }) => {
  const {
    formState: { errors }
  } = useFormContext<IAccountingRule>()
  const message = get(errors, name)?.message
  if (!message) return null
  return <ConditionFieldError>{message}</ConditionFieldError>
}

export default FiltersSelector

const ConditionValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

const ConditionFieldError = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.error};
`

const StyledConditions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const EmptyConditions = styled.div<{ $invalid: boolean }>`
  border: 1px dashed
    ${({ theme, $invalid }) => ($invalid ? theme.colors.error : theme.colors.borderStrong)};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  color: ${({ theme, $invalid }) =>
    $invalid ? theme.dashboard.critical : theme.dashboard.textSubtle};
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const ChipLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`

const AddChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  height: 30px;
  padding: 0 ${({ theme }) => theme.spacing.xs}rem;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 500;
  cursor: pointer;

  svg {
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSize.px10}rem;
  }

  &:hover {
    background: ${({ theme }) => theme.dashboard.surfaceSubtle};
    border-color: ${({ theme }) => theme.dashboard.onNavyMuted};
  }
`
